import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";

import ChatWindow from "components/chat-window/chat-window";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useAiChatStatus } from "state/reactive-vars/ai-chat-status-var";
import { useGetUserWebAiChatsQuery } from "graphql/queries/get-user-web-ai-chats.generated";
import { Ai_Chats } from "graphql/generated/graphql";
import { AiChatOrigin, AiChatType } from "constants/field-values";

// Chat-history category tabs: "Chats" = is_brief false, "Briefs" = is_brief true, "All" = no filter.
type HistoryFilter = "chats" | "briefs" | "all";

const HISTORY_TABS: { key: HistoryFilter; label: string }[] = [
	{ key: "chats", label: "Chats" },
	{ key: "briefs", label: "Briefs" },
	{ key: "all", label: "All" }
];

// Brief chats are labeled by their date/time instead of a title, e.g. "Daily brief: June 12, 2026, 7:30 PM".
// Formatted in Austin, TX (Central Time) with the 12-hour clock locals use.
const formatBriefLabel = (createdAt: string): string => {
	const date = new Date(createdAt);
	const datePart = new Intl.DateTimeFormat("en-US", {
		timeZone: "America/Chicago",
		month: "long",
		day: "numeric",
		year: "numeric"
	}).format(date);
	const timePart = new Intl.DateTimeFormat("en-US", {
		timeZone: "America/Chicago",
		hour: "numeric",
		minute: "2-digit",
		hour12: true
	}).format(date);
	return `Daily brief: ${datePart}, ${timePart}`;
};

const AiChat: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const { aiChatStatusValue, updateAiChatStatus } = useAiChatStatus();
	const navigate = useNavigate();

	const { chatId = "", historyOpen = false, chatOrigin = AiChatOrigin.PRIMARY } = aiChatStatusValue;

	// Drives the back arrow. Discarding the chat is handled by useDiscardChatsOnNavigation, in the layout.
	const isSpecialtyChat = chatOrigin === AiChatOrigin.SPECIALTY;

	const [chatHistory, setChatHistory] = useState<Array<Ai_Chats>>([]);
	const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");

	const handleOnErrorsGetUserWebAiChats = useCallback((error: ApolloError) => {
		console.log("Get user Web Ai Chats Error: ", error);
	}, []);

	const handleOnCompletedGetUserWebAiChats = useCallback((data: any) => {
		// console.log("Completed user Web Ai Chats data: ", data);
		const { ai_chats } = data;
		setChatHistory(ai_chats);
	}, []);

	const {
		data: userChatsData,
		loading,
		error,
		refetch: refetchUserChats
	} = useGetUserWebAiChatsQuery({
		// The logged user starts as an id-less placeholder; asking for the chats of user "" would
		// answer with an empty history and leave it there until the id happens to change.
		skip: !loggedUserValue.id,
		variables: {
			loggedUserId: loggedUserValue.id,
			chat_type: AiChatType.CLIENT
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorsGetUserWebAiChats,
		onCompleted: handleOnCompletedGetUserWebAiChats
	});

	// The history is fetched once on mount, so a chat created after that — the brief the cron just
	// emailed, or one generated in this session — would only appear after a reload.
	useEffect(() => {
		if (historyOpen && loggedUserValue.id) {
			refetchUserChats();
		}
	}, [historyOpen, loggedUserValue.id, refetchUserChats]);

	const handleOnOpenChat = useCallback(
		(doctrine_chat_id: string) => {
			if (chatId !== doctrine_chat_id) {
				updateAiChatStatus({
					chatId: doctrine_chat_id,
					chatMessages: [],
					chatTopic: "",
					selected_properties_ids: [],
					selected_products_ids: [],
					selected_installed_products_ids: [],
					installed_product_id: "",
					shop_product_id: "",
					guide_id: "",
					// The chat brings its own origin once its messages load; primary until then, so navigating
					// away mid-load can't discard it.
					chatOrigin: AiChatOrigin.PRIMARY,
					historyOpen: false
				});
			}
		},
		[updateAiChatStatus, chatId]
	);

	// Deep-link support: when the route is opened with ?chatId=... (e.g. from a Brief email)
	const [searchParams] = useSearchParams();
	const urlChatId = searchParams.get("chatId");

	useEffect(() => {
		if (urlChatId) {
			handleOnOpenChat(urlChatId);
		}
	}, [urlChatId, handleOnOpenChat]);

	const toggleHistory = useCallback(() => {
		updateAiChatStatus({
			historyOpen: !historyOpen
		});
	}, [updateAiChatStatus, historyOpen]);

	const chatHistoryElements = useMemo(() => {
		const { ai_chats = [] } = userChatsData || {};

		const visibleChats = ai_chats.filter(chat => {
			if (process.env.REACT_APP_FF_BRIEF_ENABLED !== "true" && chat.is_brief) {
				return false;
			}
			return historyFilter === "all" ? true : historyFilter === "briefs" ? chat.is_brief : !chat.is_brief;
		});

		const today: JSX.Element[] = [];
		const previous10: JSX.Element[] = [];
		const previous30: JSX.Element[] = [];
		const olders: JSX.Element[] = [];

		visibleChats.forEach(element => {
			const { id, doctrine_chat_id, starter_message, doctrine_title, created_at, is_brief } = element || {};

			const createdAtTime = new Date(created_at).getTime();
			const now = new Date().getTime();
			const diff = now - createdAtTime;

			const hours = Math.floor(diff / (1000 * 60 * 60));
			const days = Math.floor(diff / (1000 * 60 * 60 * 24));

			// Briefs use a date/time label; regular chats use doctrine_title, falling back to starter_message.
			const chatTitle = is_brief ? formatBriefLabel(created_at) : doctrine_title || starter_message || "";

			// Don't truncate the brief label (it's a fixed, meaningful date/time).
			const shortChatTitle = !is_brief && chatTitle.length > 33 ? chatTitle.substring(0, 33) + "..." : chatTitle;

			const chatElement = (
				<span
					key={`side-history-item-${id}`}
					className="ai-chat-side-history-item"
					onClick={() => handleOnOpenChat(doctrine_chat_id)}
				>
					{shortChatTitle}
				</span>
			);

			if (hours < 24) {
				today.push(chatElement);
			} else if (days <= 10 && days >= 1) {
				previous10.push(chatElement);
			} else if (days < 30) {
				previous30.push(chatElement);
			} else {
				olders.push(chatElement);
			}
		});

		const sideChatsHistory = (
			<>
				{today.length > 0 ? (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">{"Today"}</span>
						{today.map(element => element)}
					</div>
				) : null}
				{previous10.length > 0 ? (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">{"Previous 10 days"}</span>
						{previous10.map(element => element)}
					</div>
				) : null}
				{previous30.length > 0 ? (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">{"Previous 30 Days"}</span>
						{previous30.map(element => element)}
					</div>
				) : null}
				{olders.length > 0 ? (
					<div className="ai-chat-side-history-section">
						<span className="ai-chat-side-history-title">{"Older than 30 days"}</span>
						{olders.map(element => element)}
					</div>
				) : null}
			</>
		);

		return (
			<div className="ai-chat-side-container">
				<div className="ai-chat-side-header">
					<span className="ai-chat-side-header-title">Chat History</span>
					<img
						src={process.env.PUBLIC_URL + "/assets/images/icons/x.svg"}
						className="ai-chat-side-header-close-icon"
						onClick={() => toggleHistory()}
					/>
				</div>

				{process.env.REACT_APP_FF_BRIEF_ENABLED === "true" && (
					<div className="ai-chat-side-history-tabs">
						{HISTORY_TABS.map(tab => (
							<span
								key={`history-tab-${tab.key}`}
								className={`ai-chat-side-history-tab ${historyFilter === tab.key ? "active" : ""}`}
								onClick={() => setHistoryFilter(tab.key)}
							>
								{tab.label}
							</span>
						))}
					</div>
				)}

				{visibleChats.length > 0 ? (
					sideChatsHistory
				) : (
					<span className="ai-chat-side-history-empty">No chats to show.</span>
				)}
			</div>
		);
	}, [userChatsData, historyFilter, handleOnOpenChat, toggleHistory]);

	return (
		<div className="products-screen-container">
			<div className="ai-chat-container">
				{isMobile ? (
					<div className="ai-chat-content-container">
						{historyOpen ? (
							chatHistoryElements
						) : (
							<ChatWindow onBack={isSpecialtyChat ? () => navigate(-1) : undefined} />
						)}
					</div>
				) : (
					<>
						<div className="ai-chat-content-container">
							<ChatWindow onBack={isSpecialtyChat ? () => navigate(-1) : undefined} />
							{historyOpen ? chatHistoryElements : null}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default AiChat;
